import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { Site } from '../../../../../shared/models/site.model';
import { Venue } from '../../../../../shared/models/venue.model';
import { EventMaster } from '../../../../../shared/models/eventmaster.model';
import { Event } from '../../../../../shared/models/event.model';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../../../../../environments/environment';
import { map } from 'rxjs/operators';
import * as moment from 'moment';
import { ReturnCode } from '../../../../../shared/enums/returncode.enum';
import { formatDate } from '@angular/common';
import { EntityMapperService } from './entity-mapper.service';
import { SiteHoursModel } from '../models/site-hours.model';
import { Snapshot } from '../models/snapshot.model';
import { SnapshotHeader } from '../models/snapshot-header.model';
import { EventMonitorInfo } from '../models/event-monitor-info';
import { ScheduleValidationReportModel } from '../models/schedule-validation-report-model';
import { TranslateService } from '@ngx-translate/core';
import { BaseService } from '../../../../../shared/services/base.service';
import { BiggerPictureMessagesService } from '../../../../../shared/services/bigger-picture-messages.service';
import { MOI } from '../../../../../shared/enums/moi.enum';
import { SchedulerMessagesEnum } from '../enum/scheduler-messages.enum';
import { DatesConst } from '../../../../../shared/consts/dates';
import { BiggerPictureDatePipe } from '../../../../../shared/pipes/bigger-picture-date.pipe';
import Swal from 'sweetalert2';
import { ScheduleWarningType } from '../enum/scheduler.enum';
import colorsPalette from '../../../../../../assets/data/colors-palette.json';


@Injectable({
  providedIn: 'root'
})
export class ScheduleManagerService extends BaseService {


  private readonly sitesSource = new BehaviorSubject<Site[]>([]);
  private readonly selectedSiteSource = new BehaviorSubject<Site>(null);
  private readonly venueSource = new BehaviorSubject<Venue[]>([]);
  private readonly eventMastersSource = new BehaviorSubject<EventMaster[]>([]);
  private readonly eventSource = new BehaviorSubject<Event[]>([]);
  private readonly loadingSource = new Subject<boolean>();
  private readonly businessDateSource = new BehaviorSubject<Date>(null);
  private readonly scheduleModeSource = new BehaviorSubject<number>(2);
  private readonly allowOverlappingSource = new BehaviorSubject<boolean>(false);

  readonly sites$ = this.sitesSource.asObservable();
  readonly selectedSite$ = this.selectedSiteSource.asObservable();
  readonly venues$ = this.venueSource.asObservable();
  readonly eventMasters$ = this.eventMastersSource.asObservable();
  readonly event$ = this.eventSource.asObservable();
  readonly businessDate$ = this.businessDateSource.asObservable();
  readonly loading$ = this.loadingSource.asObservable();
  readonly scheduleMode$ = this.scheduleModeSource.asObservable();
  readonly allowOverlapping$ = this.allowOverlappingSource.asObservable();

  schedulerMessagesEnum = SchedulerMessagesEnum;

  constructor(private http: HttpClient, private entityMapper: EntityMapperService,
              translate: TranslateService, biggerPictureMessagesService: BiggerPictureMessagesService,
              private biggerPictureDatePipe: BiggerPictureDatePipe) {
    super();
    const currentUser: any = JSON.parse(sessionStorage.getItem('USER'));
    const userpermission = ',' + currentUser.userPermissions + ',';
    if (userpermission.indexOf(',831,') === -1) { this.scheduleModeSource.next(1); }
  }

  //region Getters Setters
  private set setSelectedSite(site: Site) {
    this.selectedSiteSource.next(site);
  }

  get selectedSite(): Site {
    return this.selectedSiteSource.getValue();
  }

  set allowOverlapping(value: boolean) {
    this.allowOverlappingSource.next(value);
  }

  get allowOverlapping(): boolean {
    return this.allowOverlappingSource.getValue();
  }

  set businessDate(businessDate: Date) {
    this.businessDateSource.next(businessDate);
  }

  get businessDate(): Date {
    return this.businessDateSource.getValue();
  }

  private set setScheduleMode(scheduleMode: number) {
    this.scheduleModeSource.next(scheduleMode);
  }

  get scheduleMode(): number {
    return this.scheduleModeSource.getValue();
  }

  get isPlanningMode() {
    return this.scheduleModeSource.getValue() === 2;
  }

  set events(events: Event[]) {
    const imEvents = events.map(e => ({...e}));
    // assign color
    imEvents.forEach((e) => {
      const em = this.getEventMasterByIdentifier(e.eventMasterIdentifier);
      if (em) {
        e.color = em.color;
      }
    });
    this.eventSource.next(imEvents);
    this.updateScheduledEventMasters();
  }

  public getEventMasterByIdentifier(eventMasterIdentifier: string): EventMaster {
    const eventMaster = this.eventMasters.find(em => (em.identifier === eventMasterIdentifier));
    return eventMaster;
  }

  get events(): Event[] {
    return this.eventSource.getValue();
  }

  set eventMasters(eventMasters: EventMaster[]) {
    this.eventMastersSource.next(eventMasters);
  }

  get eventMasters(): EventMaster[] {
    return this.eventMastersSource.getValue();
  }

  /**
   * HC-1095 — for a special event with an alternate name, return "AlternateName | MovieName".
   * The server may pass just the alt (auto-assign / auto-schedule popups) or the already-coalesced
   * `ISNULL(SE.EventMasterAlternateName, EM.Name)` (scheduled-event tile). When the resolved
   * name differs from the real movie's name in the cached event-master list, we append the
   * movie name. Returns `name` unchanged when there's no eventMasterId, no cache hit, or the
   * resolved name already equals the movie name. Pass `isSpecialEvent=false` to skip the lookup.
   */
  resolveSpecialEventDisplayName(name: string, eventMasterId: number, isSpecialEvent?: boolean): string {
    const base = name || '';
    if (!eventMasterId || isSpecialEvent === false) { return base; }
    const em = this.eventMasters.find(m => m.eventMasterId === eventMasterId && !m.specialEventId);
    if (em && em.name && em.name !== base) {
      return base ? `${base} | ${em.name}` : em.name;
    }
    return base;
  }

  set venues(venues: Venue[]) {
    // if(venues.length)
    //   venues[0].tooltip="First line&#13;Second line";
    this.venueSource.next(venues);
  }

  get venues(): Venue[] {
    return this.venueSource.getValue();
  }

  set sites(sites: Site[]) {
    this.sitesSource.next(sites);
  }

  get sites(): Site[] {
    return this.sitesSource.getValue();
  }

  //endregion


  init(): Promise<any> {

    const promise = new Promise<void>(async (resolve, reject) => {
      try {
        const sites: Site[] = await this.apiGetSites().toPromise();
        if (!sites.length) { // no sites
          reject('sites not found');
        } else {
          this.sites = sites;
          const site: Site = sites[0];
          this.setSelectedSite = site;

          if (this.businessDate === null) {
            // this is the first call, need to setup defaults
            const selectedBusinessDate: Date = new Date();
            selectedBusinessDate.setHours(0, 0, 0, 0);
            this.businessDate = selectedBusinessDate;
          }


          this.initSite().then(() => {
            resolve();
          }).catch((err) => {
            reject('initSite err:' + err);
          });

        }
      } catch (err) {
        reject(err);
      }
    });
    return promise;
  }

  reloadEvents(): Promise<void> {
    return new Promise<void>(async (resolve, reject) => {
      try {
        this.events = [];
        const getEvents = await this.apiGetEvents(this.selectedSite.siteId, this.businessDate, this.scheduleMode).toPromise<Event[]>();
        this.events = getEvents.map(e => this.entityMapper.mapEventFromApi(e));
        resolve();
      } catch (e) {
        // console.log('initSite', e);
        reject('reloadEvents error');
      }
    });
  }

  private initSite() {

    const promise = new Promise<void>(async (resolve, reject) => {
      try {
        this.venues = [];
        this.eventMasters = [];
        this.events = [];
        const getVenues = this.apiGetVenues(this.selectedSite.siteId).toPromise<Venue[]>();
        const getEventMasters = this.apiGetEventMasters(this.selectedSite.siteId, this.businessDate).toPromise<EventMaster[]>();
        const getEvents = this.apiGetEvents(this.selectedSite.siteId, this.businessDate, this.scheduleMode).toPromise<Event[]>();
        const res = await Promise.all([getVenues, getEventMasters, getEvents]);
        this.venues = res[0].map(this.entityMapper.mapVenueFromApi);

        const eventMasters: EventMaster[] = res[1];
        eventMasters.forEach((em, index) => {
          // em.color =  '#' + Math.floor(Math.random() * 16777215).toString(16);
          em.color = colorsPalette[index];
          em.identifier = em.eventMasterId + '-' + em.specialEventId;
        });
        this.eventMasters = eventMasters;
        // const event: Event[] = res[2];
        this.events = res[2].map(e => this.entityMapper.mapEventFromApi(e));
        resolve();
      } catch (e) {
        // console.log('initSite', e);
        reject('initSite error');
      }

    });
    return promise;
  }

  getRandomColor(): string {
    return '#' + Math.floor(Math.random() * 16777215).toString(16);
  }

  async changeBusinessDate(businessDate: Date) {
    this.businessDate = businessDate;
    await this.initSite();
  }

  async changeScheduleMode(mode: number) {
    this.setScheduleMode = mode;
    await this.initSite();
  }

  async changeSite(siteId: number) {
    const site: Site = this.sites.find(s => s.siteId === siteId);
    if (site) {
      this.setSelectedSite = site;
      await this.initSite();
    }
  }

  async reloadSchedule() {
    await this.initSite();
  }

  addEvent(event: Event) {
    const promise = new Promise<void>(async (resolve, reject) => {
      event.siteId = this.selectedSite.siteId;
      event.eventTypeId = this.scheduleMode;
      event.isActive = true;
      event.isPrivate = false;
      event.isActive = true;

      const em = this.eventMasters.find(ems => ems.eventMasterId === event.eventMasterId);
      if (em === null) {
        reject('eventmaster not found:' + event.eventMasterId);
      }

      event.intermissionLengthInMinutes = em.intermissionLengthInMinutes;

      const events = [event];
      this.apiCreateEvents(events).toPromise().then((data: any) => {
        const newEvent = this.entityMapper.mapEventFromApi(data[0]);

        this.events = [...this.events, newEvent];
        this.showScheduleWarningReport([newEvent]);
        resolve();
      }).catch((err: any) => {
        reject(this.getErrorMsg(err));
      });
    });

    return promise;
  }

  showScheduleWarningReport(events: Event[]): void {
    let specialEventsHtml = '';
    let report: string = null;

    events.forEach((event) => {
      event.warnings?.forEach((warning) => {
        switch (warning.scheduleWarningType) {
          case ScheduleWarningType.SpecialEventInvalidDateVenue:
            specialEventsHtml += `
              <div class="row">
              ${warning.data.get('eventMasterName')},  ${warning.data.get('venueName')}, ${this.biggerPictureDatePipe.transform(warning.data.get('dateTimeOfEvent'), DatesConst.FULL_DATE_WITH_HOURS)}
              </div>
              `;
            break;
        }
      });
    });

    if (specialEventsHtml) {
      specialEventsHtml = '<div class="container">' +
        '<div class="row"><b>The following special events are not scheduled as planned:</b></div>' +
        specialEventsHtml +
        '</div>';
    }

    report = specialEventsHtml;

    if (report) {
      Swal.fire({
        title: 'Warning',
        // icon: 'info',
        html: report,
        showCloseButton: true,
        showCancelButton: false,
        focusConfirm: false,
        customClass: {
          container: 'sweet-scheduleWarnings'
        }
      });
    }
  }


  addEvents(events: Event[]) {
    const promise = new Promise<void>(async (resolve, reject) => {
      if (events === null || events.length === 0) {
        resolve();
        return;
      }

      events.forEach((event: Event) => {
        event.siteId = this.selectedSite.siteId;
        event.eventTypeId = this.scheduleMode;
        event.isActive = true;
        event.isPrivate = false;
        event.isActive = true;
        const em = this.eventMasters.find(ems => ems.eventMasterId === event.eventMasterId);
        if (em === null) {
          reject('eventmaster not found:' + event.eventMasterId);
        }

        event.intermissionLengthInMinutes = em.intermissionLengthInMinutes;
      });


      this.apiCreateEvents(events).toPromise().then((data: Event[]) => {
        // const newEvent: Event = data[0];
        const newEvents = data.map(this.entityMapper.mapEventFromApi);

        this.events = [...this.events, ...newEvents];
        this.showScheduleWarningReport(newEvents);
        resolve();
      }).catch((err: any) => {
        reject(this.getErrorMsg(err));
      });
    });

    return promise;
  }


  async cancelEvent(eventId: number) {
    try {
      const event = this.events.find(e => e.eventId === eventId);
      if (event) {
        const data: any = await this.apiUpdateEvent(
          {
            siteId: event.siteId,
            eventId: event.eventId,
            isCanceled: true,
          }
        ).toPromise();
        // const updatedEvent =  this.entityMapper.mapEventFromApi(data);
        // console.log(updatedEvent);
        const events: Event[] = this.events.filter(e => e.eventId !== eventId);
        this.events = [...events];
      } else {
        throw new Error('Event not found');
      }
    } catch (error) {
      throw new Error(this.getErrorMsg(error));
    }
  }

  async togglePrivate(eventId: number) {
    try {
      const event = this.events.find(e => e.eventId === eventId);
      if (event) {
        const data: any = await this.apiUpdateEvent(
          {
            siteId: event.siteId,
            eventId: event.eventId,
            isPrivate: !event.isPrivate,
          }
        ).toPromise();
        const updatedEvent = this.entityMapper.mapEventFromApi(data);
        // console.log(updatedEvent);
        const events: Event[] = this.events.filter(e => e.eventId !== eventId);
        this.events = [...events, updatedEvent];
      } else {
        throw new Error('Event not found');
      }
    } catch (error) {
      throw new Error(this.getErrorMsg(error));
    }
  }


  async cancelBusinessDate() {
    try {
      await this.apiCancelBusinessDate(this.selectedSite.siteId, this.businessDate).toPromise();
      this.events = [];
    } catch (error) {
      throw new Error(this.getErrorMsg(error));
    }
  }

  private getBusinessDateStartTime(): Date {
    return moment(this.businessDate).add(6, 'h').toDate();
  }

  updateEvent(eventId: number, venueId: number, dateTimeOfEvent: Date) {

    const promise = new Promise<void>(async (resolve, reject) => {
      if (moment(dateTimeOfEvent).isBefore(moment(this.getBusinessDateStartTime()))) {
        reject(this.translate.instant(this.tranKey(MOI.SCHEDULE_MANAGER, this.schedulerMessagesEnum.ErrorScheduleBeforeOpening)));
        return;
      }

      const event = this.events.find(e => e.eventId === eventId);

      if (event) {

        try {
          const data: any = await this.apiUpdateEvent(
            {
              siteId: event.siteId,
              eventId: event.eventId,
              dateTimeOfEvent,
              venueId
            }
          ).toPromise();
          const updatedEvent = this.entityMapper.mapEventFromApi(data);
          // console.log(updatedEvent);
          // remove old event;
          const events: Event[] = this.events.filter(e => e.eventId !== eventId);
          this.events = [...events, updatedEvent];
          this.showScheduleWarningReport([updatedEvent]);
          resolve();
        } catch (error) {
          reject(this.getErrorMsg(error));
        }
      } else {
        reject('event not found:' + eventId);
        console.log('updateEvent missing eventid:' + eventId);
      }
    });
    return promise;
  }


  async cancelEventsByVenue(venueId: number) {
    try {
      if (!this.isPlanningMode) {
        throw new Error('option is available on planning mode');
      }
      const srcEvents = this.events.filter(e => e.venueId === venueId && !e.isCanceled);
      if (srcEvents.length > 0) {
        const targetArr: Event[] = [];
        srcEvents.forEach((e: Event) => {
          targetArr.push(
            {
              siteId: e.siteId,
              eventId: e.eventId,
              isCanceled: true
            }
          );
        });


        const data = await this.apiUpdateEvents(targetArr).toPromise();
        const updatedEvents: Event[] = [];
        // map results
        data.forEach(e => updatedEvents.push(this.entityMapper.mapEventFromApi(e)));
        // remove outdated events, use map to get a simple array of "eventid" values. Ex: [1,4]
        const filterids: number[] = updatedEvents.map(e => e.eventId);
        // Use filter and "not" includes to filter the full dataset by the filter dataset's val.
        const events: Event[] = this.events.filter(e => !filterids.includes(e.eventId));
        this.events = [...events];
      }
    } catch (error) {
      throw new Error(this.getErrorMsg(error));
    }
  }

  async handleCutPasteEvents(srcVenueId: number, targetVenueId: number) {
    try {
      if (!this.isPlanningMode) {
        throw new Error('option is available on planning mode');
      }
      const srcEvents = this.events.filter(e => e.venueId === srcVenueId && e.isActive && !e.isCanceled);
      if (srcEvents.length > 0) {
        const targetArr: Event[] = [];
        srcEvents.forEach((e: Event) => {
          targetArr.push(
            {
              siteId: e.siteId,
              eventId: e.eventId,
              venueId: targetVenueId
            }
          );
        });


        const data = await this.apiUpdateEvents(targetArr).toPromise();
        const updatedEvents: Event[] = [];
        // map results
        data.forEach(e => updatedEvents.push(this.entityMapper.mapEventFromApi(e)));
        // remove outdated events, use map to get a simple array of "eventid" values. Ex: [1,4]
        const filterids: number[] = updatedEvents.map(e => e.eventId);
        // Use filter and "not" includes to filter the full dataset by the filter dataset's val.
        const events: Event[] = this.events.filter(e => !filterids.includes(e.eventId));
        this.events = [...events, ...updatedEvents];
        this.showScheduleWarningReport((updatedEvents));
      }
    } catch (error) {
      throw new Error(this.getErrorMsg(error));
    }
  }

  getErrorMsg(err: any) {
    let msg;
    if (err.error && err.error.businessException) {
      switch (err.error.returnCode) {
        case ReturnCode.ShcedulerInsertOverlapping:
        case ReturnCode.ShcedulerUpdateOverlapping:
          const dt = formatDate(err.error.data.dateTimeOfEvent, 'dd-MM-yyyy HH:mm', 'en-US');
          return `This action will overlap with:<br>
                  Movie name: ${err.error.data.eventMasterName}<br>
                  Date time:  ${dt}`;
          break;
        case ReturnCode.ScheduleEventSequenceIsNotUnique:
          const edt = formatDate(err.error.data.dateTimeOfEvent, 'dd-MM-yyyy HH:mm', 'en-US');
          return `The event has similar sequence with:<br>
                  Movie name: ${err.error.data.eventMasterName}<br>
                  Date time:  ${edt}`;
          break;
        case ReturnCode.SchedulerFormatNotSupported:
          const format = err.error.data;
          const params = {
            format
          };
          msg = this.translate.instant(this.tranKey(this.moi.SCHEDULE_MANAGER, this.schedulerMessagesEnum.formatNotSupportedInVenue), params);
          return msg;
          break;
        case ReturnCode.ScheduleSpecialEventAlreadyScheduled:
          const data = err.error.data;
          let venueName, dateTimeOfEvent;

          if (data) {
            venueName = this.venues.find(v => v.venueId === data.venueId)?.name;
            dateTimeOfEvent = this.biggerPictureDatePipe.transform(data.dateTimeOfEvent, DatesConst.FULL_DATE_WITH_HOURS);
          }

          const seData = {
            venueName,
            dateTimeOfEvent
          };
          msg = this.translate.instant(this.tranKey(this.moi.SCHEDULE_MANAGER, this.schedulerMessagesEnum.SpecialEventAlreadyScheduled), seData);
          return msg;
          break;
        case ReturnCode.ScheduleUpdateVenueFailed:
          msg = this.translate.instant(this.tranKey(this.moi.SCHEDULE_MANAGER, this.schedulerMessagesEnum.UpdateVenueFailed));
          return msg;
          break;
        case ReturnCode.ScheduleUpdateVenueFailedAllocatedExisted:
          msg = this.translate.instant(this.tranKey(this.moi.SCHEDULE_MANAGER, this.schedulerMessagesEnum.ModifyEventWarning));
          return msg;
          break;
        case ReturnCode.ScheduleUpdateEventFailedDeallocation:
          msg = this.translate.instant(this.tranKey(this.moi.SCHEDULE_MANAGER, this.schedulerMessagesEnum.DeallocateEventError));
          return msg;
          break;
        case ReturnCode.ScheduleUpdateVenueFailedSoldExisted:
          msg = this.translate.instant(this.tranKey(this.moi.SCHEDULE_MANAGER, this.schedulerMessagesEnum.ModifyEventError));
          return msg;
          break;
        case ReturnCode.ScheduleUpdateEMOrDateTimeOfEventFailed:
          msg = this.translate.instant(this.tranKey(this.moi.SCHEDULE_MANAGER, this.schedulerMessagesEnum.UpdateEMOrTimeOfEventFailed));
          return msg;
          break;
        case ReturnCode.ScheduleCancelEventNoPermission:
          msg = this.translate.instant(this.tranKey(this.moi.SCHEDULE_MANAGER, this.schedulerMessagesEnum.NoPermissionForCancelEvent));
          return msg;
          break;
        case ReturnCode.ScheduleEventHasSales:
          msg = this.translate.instant(this.tranKey(this.moi.SCHEDULE_MANAGER, this.schedulerMessagesEnum.NoPermissionForCancelEvent));
          return msg;
          break;
        case ReturnCode.ScheduleCancelEvent:
          msg = this.translate.instant(this.tranKey(this.moi.SCHEDULE_MANAGER, this.schedulerMessagesEnum.ScheduleCancelWarning));
          return msg;
          break;

        default:
          return this.cleanHttpError(err.error.message) || err.error.returnText || err.error.name;
          break;
      }
      return err.error;
    }
    if (err.error instanceof HttpErrorResponse) {
      // A client-side or network error occurred. Handle it accordingly.
      return 'An error occurred:' + (err.error as HttpErrorResponse).status + (err.error as HttpErrorResponse).message;
    } else if (err.error) {
      return this.cleanHttpError(err.error.message) || err.error.returnText || '';
    } else {
      return this.cleanHttpError(err.message);
    }

  }

  /**
   * Strip Spring's "400 BAD_REQUEST \"actual message\"" wrapper — keep only the inner text.
   * Also strips generic "HTTP failure response for ...: 400 OK" prefixes.
   */
  private cleanHttpError(msg: string): string {
    if (!msg) return '';
    // Match: 400 BAD_REQUEST "inner text"
    const m = msg.match(/^\d{3}\s+[A-Z_]+\s+"([\s\S]*)"\s*$/);
    if (m) return m[1];
    // Match: "Http failure response for ...: 400 OK" — return a generic
    if (/^Http failure response/i.test(msg)) return msg.replace(/^Http failure response for [^:]+:\s*/i, '');
    return msg;
  }

  // err.error.businessException


  private updateScheduledEventMasters() {
    const eventMasters: EventMaster[] = this.eventMasters;
    if (eventMasters) {
      eventMasters.forEach((em) => {
        em.isScheduled = this.events.some(e => e.eventMasterIdentifier === em.identifier);
      });
    }
    this.eventMasters = eventMasters;
  }

  public restoreEventsPosition() {
    // const clonedArray: Event[] = this.events.map(e => ({ ... e }));
    // this.eventSource.next(clonedArray);
    const events: Event[] = this.events;
    events.forEach((e) => {
      e.newDateTimeOfEvent = null;
      e.newVenueId = null;
      e.newVenueName = null;
    });

    this.events = [...events];
  }


  toggleEventMasterHighlight(eventMaster: EventMaster): void {
    if (!eventMaster.highlight) {
      this.events.forEach(x => x.highlight = x.eventMasterId === eventMaster.eventMasterId);
      this.eventMasters.forEach(x => x.highlight = x === eventMaster);
    } else {
      this.events.forEach(x => x.highlight = false);
      this.eventMasters.forEach(x => x.highlight = false);
    }
    this.eventMasters = [...this.eventMasters];
    this.events = [...this.events];
  }

  toggleEventHighlight(event: Event): void {
    const eventMaster = this.eventMasters.find(em => em.eventMasterId === event.eventMasterId);

    if (!event.highlight) {
      this.events.forEach(x => x.highlight = x === event);
      this.eventMasters.forEach(x => x.highlight = x === eventMaster);
    } else {
      this.events.forEach(x => x.highlight = false);
      this.eventMasters.forEach(x => x.highlight = false);
    }

    this.eventMasters = [...this.eventMasters];
    this.events = [...this.events];
  }

  getSelectedEventMaster(): EventMaster {
    return this.eventMasters.find(evm => evm.highlight);
  }

  //region Site Hours
  public async GetSiteHours() {
    try {
      const list = await this.apiGetSiteHours(this.selectedSite.siteId).toPromise();
      const data: any[] = JSON.parse(list[0].data);
      const siteHours: SiteHoursModel[] = data.map(this.entityMapper.mapSiteHoursFromApi);
      // assign proper start end dates
      siteHours.forEach((sh) => {
        sh.open = moment('1900-01-01').add(sh.openInMinutes, 'minutes').toDate();
        sh.close = moment('1900-01-01').add(sh.closeInMinutes, 'minutes').toDate();
      });
      return siteHours;
      // this.seatPlanService.populate(seatPlan);
    } catch (e) {
      throw new Error(this.getErrorMsg(e));
    }
  }

  public async UpdateSiteHours(data: any) {
    try {
      const res = await this.apiUpdateSiteHours(this.selectedSite.siteId, JSON.stringify(data)).toPromise();
      return res;
      // this.seatPlanService.populate(seatPlan);
    } catch (e) {
      throw new Error(this.getErrorMsg(e));
    }
  }

  //endregion


  //region SNAPSHOT
  async createSnapshot(name: string) {
    try {
      const snapshot: Snapshot = {
        name,
        businessDate: this.businessDate,
        siteId: this.selectedSite.siteId
      };

      if (!snapshot.name) {
        throw new Error('snapshot name is required');
      }

      await this.apiCreateSnapshot(snapshot).toPromise();
    } catch (error) {
      throw new Error(this.getErrorMsg(error));
    }
  }


  async loadSnapshot(snapshotHeaderId: number) {
    try {
      const snapshot: Snapshot = {
        snapshotHeaderId,
        businessDate: this.businessDate,
        siteId: this.selectedSite.siteId
      };
      const events: Event[] = await this.apiLoadSnapshots(snapshot).toPromise();
      this.events = events;
    } catch (error) {
      throw new Error(this.getErrorMsg(error));
    }
  }


  //endregion


  //region HTTP
  private apiGetSites(): Observable<Site[]> {

    return this.http.get(environment.systemAPI + '/api/sys/sites')
      .pipe(map((data: any) => data.list));
  }

  public apiGetVenues(siteId: number): Observable<Venue[]> {
    return this.http.get(environment.systemAPI + `/api/sys/site/${siteId}/venues`)
      .pipe(map((data: any) => data.list));
  }

  // http://localhost:9000/api/sch/eventmasters/1025
  private apiGetEventMasters(siteId: number, businessDate: Date): Observable<EventMaster[]> {
    const url: string = environment.systemAPI + `/api/sch/eventmasters/${siteId}/businessdate/`
      + moment(businessDate).format('YYYY-MM-DD')
      + (this.scheduleMode === 2 ? '?isPlanningMode=1' : '')
    ;

    return this.http.get(url)
      .pipe(map((data: any) => data.list));
  }

  private apiGetSiteHours(siteId: number): Observable<any> {
    return this.http.get(environment.systemAPI + `/api/sch/sitehours/${siteId}`)
      .pipe(map((data: any) => data.list));
  }

  private apiUpdateSiteHours(siteId: number, data: string): Observable<any> {
    return this.http.post(environment.systemAPI + `/api/sch/sitehours/${siteId}`, data);
  }

  // http://192.168.10.150:4201/api/sch/events/siteid/1025/startdate/2020-09-06/enddate/2020-09-06/?venueId=1&eventTypeId=1&eventId=417013
  private apiGetEvents(siteId: number, businessDate: Date, eventTypeId: number): Observable<Event[]> {

    const sdate = moment(businessDate).format('YYYY-MM-DD');
    const url = environment.systemAPI + `/api/sch/events/siteid/${siteId}/startdate/${sdate}/enddate/${sdate}/?eventTypeId=${eventTypeId}&availableType=0`;
    return this.http.get(url)
      .pipe(map((data: any) => {
          const events: Event[] = (data.list) as Event[];
          events.forEach((e) => {
            e.dateTimeOfEvent = moment(e.dateTimeOfEvent).toDate();
            e.businessDate = moment(e.businessDate).toDate();
          });
          return events;

        }
      ));
  }

  private apiCancelBusinessDate(siteId: number, businessDate: Date): Observable<any> {

    const sdate = moment(businessDate).format('YYYY-MM-DD');
    const url = environment.systemAPI + `/api/sch/util/cancelbd/${siteId}/${sdate}/`;
    return this.http.get(url);
  }


  private apiCreateEvents(events: Event[]): Observable<Event[]> {
    const url = environment.systemAPI + `/api/sch/events?isOverlapEvents=${this.allowOverlapping}`;
    return this.http.post(url, events)
      .pipe(map((data: any) => data.list
      ));
  }

  private apiUpdateEvent(event: Event): Observable<Event> {
    const body = [event];
    const url = environment.systemAPI + `/api/sch/events/partialUpdate?isAutoMode=1&isOverlapEvents=${this.allowOverlapping}`;
    return this.http.post(url, body)
      .pipe(map((data: any) => data.list[0]
      ));
  }

  private apiUpdateEvents(event: Event[]): Observable<Event[]> {
    const url = environment.systemAPI + `/api/sch/events/partialUpdate?isAutoMode=1&isOverlapEvents=${this.allowOverlapping}`;
    return this.http.post(url, event)
      .pipe(map((data: any) => data.list
      ));
  }

  private apiCreateSnapshot(snapshot: Snapshot): Observable<any> {
    const url = environment.systemAPI + `/api/sch/snapshots`;
    return this.http.post(url, snapshot);
  }

  public apiGetSnapshots(siteId: number, businessDate: Date): Observable<SnapshotHeader[]> {

    const sdate = moment(businessDate).format('YYYY-MM-DD');
    const url = environment.systemAPI + `/api/sch/snapshots/${siteId}/${sdate}/`;
    return this.http.get(url).pipe(
      map((data: any) => data.list)
    );
  }

  private apiLoadSnapshots(snapshot: Snapshot): Observable<Event[]> {

    const url = environment.systemAPI + `/api/sch/snapshot/load`;
    return this.http.post(url, snapshot)
      .pipe(map((data: any) => (data.list as any[]).map(this.entityMapper.mapEventFromApi)
      ));
  }

  public apiGetMonitorInfo(siteId: number, businessDate: Date): Observable<EventMonitorInfo[]> {
    const sdate = moment(businessDate).format('YYYY-MM-DD');
    const url = environment.systemAPI + `/api/sch/monitor/${siteId}/${sdate}`;
    return this.http.get<any>(url).pipe(map((res: any) => res.list));
  }

  public apiDeploySchedule(siteId: number, startDate: Date, endDate: Date, skipOverlaps: boolean = false): Observable<any> {
    const deployRequest = {
      siteId,
      startDate,
      endDate,
      skipOverlaps
    };

    const url = environment.systemAPI + `/api/sch/deploy`;
    return this.http.post<any>(url, deployRequest).pipe(map((res: any) => res.list[0]));
  }

  public apiDeployScheduleSmart(siteId: number, startDate: Date, endDate: Date, skipOverlaps: boolean = false, isTest: boolean = false): Observable<any> {
    const deployRequest: any = {
      siteId,
      startDate,
      endDate,
      skipOverlaps
    };
    if (isTest) { deployRequest.isTest = 1; }

    const url = environment.systemAPI + `/api/sch/deploy/smart`;
    return this.http.post<any>(url, deployRequest).pipe(map((res: any) => res.list[0]));
  }

  public apiGetScheduleComparisonInfo(siteId: number, fromBusinessDate: Date, toBusinessDate: Date): Observable<Event[]> {
    const sdate = moment(fromBusinessDate).format('YYYY-MM-DD');
    const edate = moment(toBusinessDate).format('YYYY-MM-DD');
    const url = environment.systemAPI + `/api/sch/comparison/${siteId}/${sdate}/${edate}`;
    return this.http.get<any>(url).pipe(map((res: any) => res.list));
  }

  public apiGetScheduleValidationReport(): Observable<ScheduleValidationReportModel> {
    const businessDateStr = moment(this.businessDate).format('YYYY-MM-DD');
    const url = environment.systemAPI + `/api/sch/validate/${this.selectedSite.siteId}/${businessDateStr}/${this.scheduleMode}`;
    return this.http.get<any>(url).pipe(map((res: any) => res.list[0]));
  }

  //endregion


  // tslint:disable-next-line:typedef
  async autoScheduleSpecialEvents(startDate: Date = null, endDate: Date = null): Promise<any> {
    try {
      const result = await this.apiAutoScheduleSpecialEvents(this.selectedSite.siteId, startDate || this.businessDate, endDate).toPromise();
      this.events = [];
      return result;
    } catch (error) {
      throw new Error(this.getErrorMsg(error));
    }
  }

  public apiAutoScheduleSpecialEvents(siteId: number, businessDate: Date,
                                     endBusinessDate: Date = null): Observable<any> {
    const sdate = moment(businessDate).format('YYYY-MM-DD');
    let url = environment.systemAPI + `/api/sch/events/autoSpecialEvent/${siteId}/${sdate}/`;
    let edate = null;
    if (endBusinessDate) {
      edate = moment(endBusinessDate).format('YYYY-MM-DD');
      url = environment.systemAPI + `/api/sch/events/autoSpecialEvent/${siteId}/${sdate}/${edate}/`;
    }
    const seRequest = {siteId, businessDate};
    return this.http.post<any>(url, seRequest);
  }

  /**
   * HC-1095 batch auto-assign: attaches unscheduled special events to existing programmed events.
   * @param siteId  specific site, or 0/null for all sites under HO
   * @param startDate  start of range (null → SP defaults to next 14 days)
   * @param endDate    end of range
   * @param isTest     true → preview only, false → perform assignment
   */
  public async autoAssignSpecialEvents(siteId: number, startDate: Date, endDate: Date, isTest: boolean): Promise<any> {
    try {
      const url = environment.systemAPI + '/api/sys/specialevent/autoAssign';
      const params: any = { isTest: String(isTest) };
      if (siteId) { params.sId = String(siteId); }
      if (startDate) { params.startDate = moment(startDate).format('YYYY-MM-DD'); }
      if (endDate) { params.endDate = moment(endDate).format('YYYY-MM-DD'); }
      return await this.http.post<any>(url, null, { params }).toPromise();
    } catch (error) {
      throw new Error(this.getErrorMsg(error));
    }
  }


  async isEventHasSales(eventId: number) {
    try {
      const event = this.events.find(e => e.eventId === eventId);
      if (event) {
        const data: any = await this.apiIsEventHasSales(
          {
            siteId: event.siteId,
            eventId: event.eventId,
          }
        ).toPromise();
      }
    } catch (error) {
      throw error;
    }
  }

  private apiIsEventHasSales(event: Event): Observable<Event> {
    const body = [event];
    const url = environment.systemAPI + `/api/sch/events/isEventHasSales`;
    return this.http.post(url, body)
      .pipe(map((data: any) => data
      ));
  }

   apiProgressStatus(siteId: number): Observable<any> {
    return this.http.get(environment.systemAPI + `/api/sch/events/deployStatus`)
      .pipe(map((data: any) => data));
  }


}
